import React, { useState } from "react";
import { injectIntl, IntlShape } from "react-intl";
import { gql, useQuery, useMutation } from "@apollo/client";
import { styled, useTheme } from "@material-ui/styles";
import { Paper, TextField, ButtonGroup, Theme } from "@material-ui/core";
import moment from "moment";
import Typography from "../typography";
import Button from "../button";
import GenericSelect from "../atoms/GenericSelect";
import { getSkipReasonLabels } from "../pre-season-shared/constants";
import { IStyledArguments } from "../../types/styled-arguments";
import messages from "./PreSeasonEntriesCard.intl";

export const MY_PRE_SEASON_ENTRIES = gql`
  query MyPreSeasonEntries($to: String) {
    myPreSeasonEntries(to: $to) {
      id
      date
      status
      reason
      reasonNote
      timeMinutes
      distanceKm
      sets
      reps
      weightKg
      note
      exercise {
        id
        title
        type
      }
    }
  }
`;

export const SUBMIT_PRE_SEASON_ENTRY = gql`
  mutation SubmitPreSeasonEntry($entryId: ID!, $input: PreSeasonEntryInput!) {
    submitPreSeasonEntry(entryId: $entryId, input: $input) {
      id
      status
      reason
      reasonNote
      timeMinutes
      distanceKm
      sets
      reps
      weightKg
      note
      completedAt
    }
  }
`;

const CardWrapper = styled(Paper)(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  borderRadius: 16,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  padding: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    borderRadius: 14,
    padding: theme.spacing(2),
  },
}));

const EntryCard = styled("div")(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  borderRadius: "16px",
  background: theme.palette.extra.backgroundGrey,
  border: `1px solid ${theme.palette.extra.borderColor}`,
}));

const FieldsRow = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(2),
  marginTop: theme.spacing(1.5),
}));

interface IPreSeasonEntry {
  id: string;
  date: string;
  status: "PENDING" | "DONE" | "NOT_DONE";
  reason?: string;
  reasonNote?: string;
  timeMinutes?: number;
  distanceKm?: number;
  sets?: number;
  reps?: number;
  weightKg?: number;
  note?: string;
  exercise: { id: string; title: string; type: string };
}

const PreSeasonEntryRow = injectIntl<{ entry: IPreSeasonEntry }>(
  ({ entry, intl }) => {
    const [submitEntry, { loading, data }] = useMutation(
      SUBMIT_PRE_SEASON_ENTRY
    );
    const [choice, setChoice] = useState<"DONE" | "NOT_DONE" | undefined>(
      entry.status !== "PENDING" ? (entry.status as any) : undefined
    );
    const [timeMinutes, setTimeMinutes] = useState(entry.timeMinutes || "");
    const [distanceKm, setDistanceKm] = useState(entry.distanceKm || "");
    const [sets, setSets] = useState(entry.sets || "");
    const [reps, setReps] = useState(entry.reps || "");
    const [weightKg, setWeightKg] = useState(entry.weightKg || "");
    const [note, setNote] = useState(entry.note || "");
    const [reason, setReason] = useState(entry.reason || "");
    const [reasonNote, setReasonNote] = useState(entry.reasonNote || "");

    const reasonLabels = getSkipReasonLabels(intl);
    const justSaved = !!data;

    const handleSave = async () => {
      if (!choice) return;
      await submitEntry({
        variables: {
          entryId: entry.id,
          input: {
            status: choice,
            reason: choice === "NOT_DONE" ? reason || undefined : undefined,
            reasonNote:
              choice === "NOT_DONE" && reason === "OTHER"
                ? reasonNote || undefined
                : undefined,
            timeMinutes:
              choice === "DONE" && entry.exercise.type === "RUNNING"
                ? Number(timeMinutes) || undefined
                : undefined,
            distanceKm:
              choice === "DONE" && entry.exercise.type === "RUNNING"
                ? Number(distanceKm) || undefined
                : undefined,
            sets:
              choice === "DONE" && entry.exercise.type === "STRENGTH"
                ? Number(sets) || undefined
                : undefined,
            reps:
              choice === "DONE" && entry.exercise.type === "STRENGTH"
                ? Number(reps) || undefined
                : undefined,
            weightKg:
              choice === "DONE" && entry.exercise.type === "STRENGTH"
                ? Number(weightKg) || undefined
                : undefined,
            note:
              choice === "DONE" && entry.exercise.type === "GENERIC"
                ? note || undefined
                : undefined,
          },
        },
      });
    };

    return (
      <EntryCard>
        <Typography.Subtitle1 style={{ fontWeight: 700 }}>
          {entry.exercise.title}
        </Typography.Subtitle1>
        <Typography.Subtitle1 style={{ color: "#666", fontSize: "0.85rem" }}>
          {moment(entry.date).format("dddd DD/MM")}
        </Typography.Subtitle1>

        <ButtonGroup style={{ marginTop: 12 }} color="primary">
          <Button
            variant={choice === "DONE" ? "contained" : "outlined"}
            color="primary"
            onClick={() => setChoice("DONE")}
          >
            {intl.formatMessage(messages.done)}
          </Button>
          <Button
            variant={choice === "NOT_DONE" ? "contained" : "outlined"}
            color="secondary"
            onClick={() => setChoice("NOT_DONE")}
          >
            {intl.formatMessage(messages.notDone)}
          </Button>
        </ButtonGroup>

        {choice === "DONE" && entry.exercise.type === "RUNNING" && (
          <FieldsRow>
            <TextField
              type="number"
              label={intl.formatMessage(messages.timeMinutes)}
              value={timeMinutes}
              onChange={(e) => setTimeMinutes(e.target.value as any)}
            />
            <TextField
              type="number"
              label={intl.formatMessage(messages.distanceKm)}
              value={distanceKm}
              onChange={(e) => setDistanceKm(e.target.value as any)}
            />
          </FieldsRow>
        )}

        {choice === "DONE" && entry.exercise.type === "STRENGTH" && (
          <FieldsRow>
            <TextField
              type="number"
              label={intl.formatMessage(messages.sets)}
              value={sets}
              onChange={(e) => setSets(e.target.value as any)}
            />
            <TextField
              type="number"
              label={intl.formatMessage(messages.reps)}
              value={reps}
              onChange={(e) => setReps(e.target.value as any)}
            />
            <TextField
              type="number"
              label={intl.formatMessage(messages.weightKg)}
              value={weightKg}
              onChange={(e) => setWeightKg(e.target.value as any)}
            />
          </FieldsRow>
        )}

        {choice === "DONE" && entry.exercise.type === "GENERIC" && (
          <FieldsRow>
            <TextField
              fullWidth
              multiline
              label={intl.formatMessage(messages.note)}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </FieldsRow>
        )}

        {choice === "NOT_DONE" && (
          <FieldsRow>
            <GenericSelect
              id={`reason-${entry.id}`}
              label={intl.formatMessage(messages.reason)}
              collection={reasonLabels}
              value={reason}
              onChange={(e: any) => {
                if (e) setReason(e.target.value);
              }}
            />
            {reason === "OTHER" && (
              <TextField
                fullWidth
                placeholder={intl.formatMessage(
                  messages.reasonOtherPlaceholder
                )}
                value={reasonNote}
                onChange={(e) => setReasonNote(e.target.value)}
              />
            )}
          </FieldsRow>
        )}

        {choice && (
          <div style={{ marginTop: 12 }}>
            <Button
              variant="contained"
              color="primary"
              disabled={
                loading || (choice === "NOT_DONE" && reason === "")
              }
              onClick={handleSave}
            >
              {justSaved
                ? intl.formatMessage(messages.saved)
                : intl.formatMessage(messages.save)}
            </Button>
          </div>
        )}
      </EntryCard>
    );
  }
);

interface IPreSeasonEntriesCardProps {
  intl: IntlShape;
}

const PreSeasonEntriesCard = injectIntl<Omit<IPreSeasonEntriesCardProps, "intl">>(
  ({ intl }) => {
    const theme = useTheme<Theme>();
    const { data } = useQuery(MY_PRE_SEASON_ENTRIES, {
      variables: { to: moment().format("YYYY-MM-DD") },
    });
    const [showHistory, setShowHistory] = useState(false);

    const entries: IPreSeasonEntry[] = (data && data.myPreSeasonEntries) || [];
    const pending = entries.filter((e) => e.status === "PENDING");
    const history = entries.filter((e) => e.status !== "PENDING");

    if (entries.length === 0) {
      return null;
    }

    return (
      <CardWrapper>
        <Typography.H6 style={{ fontWeight: 800, color: theme.palette.primary.main }}>
          {intl.formatMessage(messages.title)}
        </Typography.H6>

        {pending.length === 0 && history.length === 0 && (
          <Typography.Subtitle1 style={{ color: "#666" }}>
            {intl.formatMessage(messages.empty)}
          </Typography.Subtitle1>
        )}

        {pending.map((entry) => (
          <PreSeasonEntryRow key={entry.id} entry={entry} />
        ))}

        {history.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <Button variant="outlined" onClick={() => setShowHistory((s) => !s)}>
              {showHistory
                ? intl.formatMessage(messages.hideHistory)
                : intl.formatMessage(messages.showHistory)}
            </Button>
            {showHistory &&
              history.map((entry) => (
                <PreSeasonEntryRow key={entry.id} entry={entry} />
              ))}
          </div>
        )}
      </CardWrapper>
    );
  }
);

export default PreSeasonEntriesCard;
